import { useState, useEffect, useCallback, useRef } from 'react';
import { TextInput, Loader, Alert, Text, Button, Card, Group, Stack, Pagination } from '@mantine/core';
import { User, useAuthInfo } from '@propelauth/react';
import { useGroups } from '../../hooks/useGroups';
import { GroupCard } from './GroupCard';
import { CreateGroupModal } from './CreateGroupModal';
import { GroupDetailView } from './GroupDetailView';
import searchIcon from '../../assets/icons/search.svg';
import { GroupResponse, GroupBalance } from '../../types/group';
import { env } from '../../utils/env';

// Simple utility for conditional logging
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

interface GroupsListProps {
  accessToken: string;
  currentUser: User;
  isVisible?: boolean;
}

interface GroupWithBalances extends GroupResponse {
  balances: GroupBalance[];
}

export const GroupsList = ({ accessToken, currentUser, isVisible = true }: GroupsListProps) => {
  // Get auth info directly as a fallback
  const authInfo = useAuthInfo();
  const [token, setToken] = useState<string>(accessToken || '');

  // Track if balances are currently being fetched
  const isFetchingBalances = useRef(false);

  // Try to get token if it wasn't passed as prop
  useEffect(() => {
    if (!accessToken && authInfo?.accessToken) {
      debugLog('[GroupsList] Using accessToken from useAuthInfo');
      setToken(authInfo.accessToken);
    } else if (accessToken) {
      debugLog('[GroupsList] Using accessToken from props');
      setToken(accessToken);
    } else {
      console.error('[GroupsList] No accessToken available');
    }
  }, [accessToken, authInfo]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const {
    groups,
    loading,
    error,
    refresh,
    deleteGroup,
    currentPage,
    totalPages,
    handlePageChange
  } = useGroups(token, { isVisible });
  const [groupsWithBalances, setGroupsWithBalances] = useState<GroupWithBalances[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Debug logging
  useEffect(() => {
    debugLog('[GroupsList] Token present:', !!token);
    debugLog('[GroupsList] Token length:', token?.length || 0);
  }, [token]);

  // Fetch balances for each group - memoized with stable reference
  const fetchGroupBalances = useCallback(async () => {
    // If already fetching balances or no groups/token, do nothing
    if (!groups || groups.length === 0 || !token || isFetchingBalances.current) return;

    debugLog('[GroupsList] Fetching balances for', groups.length, 'groups');
    setBalancesLoading(true);
    isFetchingBalances.current = true;

    try {
      // Create an array of promises for fetching balances for each group
      const balancePromises = groups.map(async (group) => {
        try {
          debugLog(`[GroupsList] Fetching details for group ${group.id}`);
          const response = await fetch(`${env.API_URL}/api/groups/${group.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            credentials: 'include',
          });

          if (!response.ok) {
            console.error(`[GroupsList] Failed to fetch group detail for ${group.id}`);
            return { ...group, balances: [] };
          }

          const data = await response.json();
          debugLog(`[GroupsList] Received balances for group ${group.id}:`, data.balances?.length || 0);
          return {
            ...group,
            balances: data.balances || []
          };
        } catch (err) {
          console.error(`[GroupsList] Error fetching balances for group ${group.id}:`, err);
          return { ...group, balances: [] };
        }
      });

      // Wait for all promises to resolve
      const updatedGroups = await Promise.all(balancePromises);
      debugLog('[GroupsList] Updated all group balances:', updatedGroups.length);
      setGroupsWithBalances(updatedGroups);
    } catch (err) {
      console.error('[GroupsList] Error fetching group balances:', err);
    } finally {
      setBalancesLoading(false);
      isFetchingBalances.current = false;
    }
  }, [groups, token]);

  // Handle group deletion
  const handleGroupDeleted = useCallback((groupId: number) => {
    // Update our local state to remove the deleted group immediately
    setGroupsWithBalances(prev => prev.filter(group => group.id !== groupId));
  }, []);

  // Create a wrapper for deleteGroup that matches the expected interface
  const handleDeleteGroup = useCallback(
    async (groupId: number) => {
      return await deleteGroup(groupId);
    },
    [deleteGroup]
  );

  // Initial data load when groups change - with better change detection
  useEffect(() => {
    if (groups && groups.length > 0 && token && !isFetchingBalances.current && isVisible) {
      // Only trigger a balance fetch if the group IDs have actually changed
      const currentGroupIds = JSON.stringify(groupsWithBalances.map(g => g.id).sort());
      const newGroupIds = JSON.stringify(groups.map(g => g.id).sort());

      if (currentGroupIds !== newGroupIds) {
        fetchGroupBalances();
      }
    }
  }, [groups, token, fetchGroupBalances, groupsWithBalances, isVisible]);

  // Only filter groups when they exist and we're not in detail view
  const filteredGroups = !selectedGroupId && groupsWithBalances && Array.isArray(groupsWithBalances)
    ? groupsWithBalances.filter((group) => {
      // If no search query, show all groups
      if (!searchQuery || searchQuery.trim() === '') {
        return true;
      }

      // Otherwise filter by name if it exists
      const groupName = group?.name || '';
      return groupName.toLowerCase().includes(searchQuery.toLowerCase());
    })
    : [];

  const handleSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  const handleBackToGroups = () => {
    setSelectedGroupId(null);
  };

  // Render the detail view when a group is selected
  if (selectedGroupId) {
    return (
      <div className="groups-list-container">
        <Button
          variant="subtle"
          onClick={handleBackToGroups}
          leftSection="←"
          mb="md"
        >
          Back to Groups
        </Button>
        <GroupDetailView
          groupId={selectedGroupId}
          accessToken={token}
          onRefresh={refresh}
          isVisible={isVisible}
        />
      </div>
    );
  }

  // Otherwise render the list view
  return (
    <div className="groups-list-container">
      <Group justify="space-between" mb="md">
        <Text size="xl" fw={700}>My Groups</Text>
        <Button
          onClick={() => setShowCreateModal(true)}
          leftSection={<Text>+</Text>}
        >
          Create Group
        </Button>
      </Group>

      <TextInput
        placeholder="Search groups..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        leftSection={<img src={searchIcon} alt="Search" width={16} />}
        mb="lg"
      />

      {loading || balancesLoading ? (
        <Loader />
      ) : error ? (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      ) : filteredGroups.length === 0 ? (
        <Card p="xl" withBorder>
          <Text ta="center" c="dimmed">
            No groups found. Create one to get started!
          </Text>
        </Card>
      ) : (
        <Stack gap="md">
          {filteredGroups.map((group: GroupWithBalances) => (
            <GroupCard
              key={group.id}
              group={group}
              onSelect={() => handleSelectGroup(group.id.toString())}
              accessToken={token}
              deleteGroup={handleDeleteGroup}
              onDeleted={handleGroupDeleted}
            />
          ))}
          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} />
            </Group>
          )}
        </Stack>
      )}

      <CreateGroupModal
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={refresh}
      />
    </div>
  );
}; 